import React from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StatusChip from "../common/StatusChip";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const TenantDetailsDrawer = ({
  open,
  tenant,
  onClose,
  onEdit,
  onOpenTenant,
}) => {
  if (!tenant) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 3 }}
      PaperProps={{
        sx: (theme) => {
          const topOffset = theme.spacing(8);
          return {
            width: { xs: "100%", sm: 440 },
            top: topOffset,
            height: `calc(100% - ${topOffset})`,
            maxHeight: `calc(100vh - ${topOffset})`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
          };
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              Tenant Details
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: "break-word", mt: 0.25 }}
            >
              {tenant.name || tenant.id}
            </Typography>
          </Box>
          <IconButton aria-label="Close details" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 3,
          pt: 2,
          pb: 2.5,
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DetailItem label="Tenant ID" value={tenant.id} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Name" value={tenant.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Contact Person" value={tenant.contactPerson} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Email" value={tenant.email} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Mobile" value={tenant.mobile} />
              </Grid>
              {tenant.dns ? (
                <Grid item xs={12}>
                  <DetailItem label="DNS" value={tenant.dns} />
                </Grid>
              ) : null}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Status
            </Typography>
            <StatusChip enabled={tenant.enabled} />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          flexShrink: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1.25}>
            {onEdit ? (
              <Button fullWidth variant="outlined" onClick={onEdit} sx={{ whiteSpace: "nowrap" }}>
                Edit
              </Button>
            ) : null}
            {onOpenTenant ? (
              <Button fullWidth variant="contained" onClick={onOpenTenant} sx={{ whiteSpace: "nowrap" }}>
                Open tenant
              </Button>
            ) : null}
          </Stack>
          <Button fullWidth variant="text" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default TenantDetailsDrawer;
